import React, { useState, useEffect } from 'react';
import axios from 'axios';
import I18n from '@/modules/i18n';
import * as Routes from '@/modules/routes';
import { QualityFeedback } from '@/components/shared/QualityFeedback';
import StandardButton from '@/components/ui/Buttons/StandardButton';
import Icon from '@/components/ui/Icon';
import Modal from '@/components/ui/Modal';
import Text from '@/components/ui/Text';
import { COLORS } from '@/components/ui/ui.constants';
import { toast } from '@/components/ui/Toast';
import cn from '@/utils/cn';
import QuestionCard from './QuestionCard';
import useAIQuestionBuilderStore from './useAIQuestionBuilderStore';
import useAIQuestionFeedback from './useAIQuestionFeedback';

const ReviewQuestionsPanel = () => {
  const {
    assessmentID,
    creationMode,
    generatedQuestions,
    jobID,
    llmCompletionID,
    saveStatus,
    selectedQuestionIDs,
    closePanel,
    deselectAllQuestions,
    openConfigurePanel,
    selectAllQuestions,
    setSaveStatus,
    toggleQuestionSelected,
  } = useAIQuestionBuilderStore();

  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const feedback = useAIQuestionFeedback(assessmentID, llmCompletionID);

  const selectedCount = selectedQuestionIDs.size;
  const totalCount = generatedQuestions.length;
  const isSaving = saveStatus === 'saving';

  const handleClose = () => {
    setConfirmCloseOpen(true);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    closePanel();
  };

  const handleSave = async () => {
    if (selectedCount === 0 || !assessmentID) return;

    const selectedQuestions = generatedQuestions.filter(q => selectedQuestionIDs.has(q.id));

    setSaveStatus('saving');

    try {
      const { data } = creationMode === 'modify'
        ? await axios.post(
          Routes.plato_api_apply_modifications_ai_question_builder_index_path(),
          {
            assessment_id: assessmentID,
            job_id: jobID,
            modifications: selectedQuestions.map(q => ({
              modified: {
                choices: q.choices,
                stem: q.stem,
              },
              question_id: q.id,
            })),
          },
        )
        : await axios.post(
          Routes.plato_api_save_ai_question_builder_index_path(),
          {
            assessment_id: assessmentID,
            job_id: jobID,
            questions: selectedQuestions.map(q => ({
              choices: q.choices,
              stem: q.stem,
            })),
          },
        );

      setSaveStatus('saved');
      const messageKey = creationMode === 'modify' ? 'ai_qb_questions_updated' : 'ai_qb_questions_added';
      sessionStorage.setItem('ai-qb-saved', I18n.t(messageKey, { count: selectedCount }));
      sessionStorage.setItem('ai-qb-saved-ids', JSON.stringify(data.question_ids));
      window.location.reload();
    } catch {
      setSaveStatus('error');
      toast.show(I18n.t('ai_qb_save_error'), { closeButton: false, toastId: 'ai-qb-save-error' });
    }
  };

  return (
    <>
      <div
        className={cn(
          'tw-fixed tw-top-0 tw-right-0 tw-w-[38.75rem] tw-h-full tw-bg-white',
          'tw-z-[1050] tw-flex tw-flex-col tw-border-l-2 tw-border-solid tw-border-grey',
          'tw-shadow-lg',
        )}
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3">
          <Text variant="heading2" weight="bold">
            {I18n.t('ai_qb_review_title')}
          </Text>
          <button
            aria-label="Close panel"
            className={cn(
              'tw-text-dark-grey tw-bg-transparent tw-border-none',
              'tw-cursor-pointer tw-w-6 tw-h-6 tw-flex tw-items-center',
              'tw-justify-center hover:tw-text-tci-black',
            )}
            type="button"
            onClick={handleClose}
          >
            <Icon icon="Close" size={16} />
          </button>
        </div>

        <div
          className={cn(
            'tw-flex tw-items-center tw-gap-4',
            'tw-px-5 tw-py-2 tw-bg-light-grey',
          )}
        >
          {llmCompletionID !== null && (
            <QualityFeedback
              disabled={feedback.isSubmitting}
              rating={feedback.rating}
              onRate={feedback.onRate}
            />
          )}
          <Text className="tw-ml-auto" size="sm" tag="span" variant="body">
            <strong className="tw-text-green">{selectedCount}</strong>
            {' '}
            {I18n.t('ai_qb_of_n_selected', { total: totalCount })}
          </Text>
          <div className="tw-flex tw-gap-2">
            <StandardButton
              outlineStyle
              small
              color="purple"
              onClick={selectAllQuestions}
            >
              {I18n.t('ai_qb_select_all_btn')}
            </StandardButton>
            <StandardButton
              outlineStyle
              small
              color="purple"
              onClick={deselectAllQuestions}
            >
              {I18n.t('ai_qb_deselect_all_btn')}
            </StandardButton>
          </div>
        </div>

        <div className="tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-3">
          {generatedQuestions.map((question, index) => (
            <QuestionCard
              key={question.id}
              dimmed={!selectedQuestionIDs.has(question.id)}
              index={index}
              question={question}
              selected={selectedQuestionIDs.has(question.id)}
              onToggle={() => toggleQuestionSelected(question.id)}
            />
          ))}
        </div>

        <hr className="tw-border-t tw-border-solid tw-border-grey tw-m-0" />

        <div className="tw-flex tw-items-center tw-px-5 tw-py-3 tw-gap-4">
          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1">
            <Icon className="tw-shrink-0" fill={COLORS['dark-grey']} icon="InfoAlternate" size={16} />
            <Text size="sm" tag="span" variant="body">
              {I18n.t('ai_qb_edit_after_save')}
            </Text>
          </div>
          <button
            className={cn(
              'tw-text-sm tw-font-tci tw-text-tci-purple tw-bg-transparent',
              'tw-border-none tw-cursor-pointer hover:tw-underline',
            )}
            type="button"
            onClick={handleClose}
          >
            {I18n.t('ai_qb_close')}
          </button>
          {selectedCount > 0 ? (
            <StandardButton
              color="purple"
              disabled={isSaving}
              onClick={handleSave}
            >
              {isSaving
                ? I18n.t('ai_qb_creating_questions')
                : I18n.t('ai_qb_save_selected', { count: selectedCount })}
            </StandardButton>
          ) : (
            <StandardButton color="purple" onClick={openConfigurePanel}>
              {I18n.t('ai_qb_start_over')}
            </StandardButton>
          )}
        </div>
      </div>

      <Modal
        close={() => setConfirmCloseOpen(false)}
        headerText={I18n.t('ai_qb_close_confirm_title')}
        isOpen={confirmCloseOpen}
      >
        <Text className="tw-px-4 tw-py-2" size="sm">
          {I18n.t('ai_qb_close_confirm_body')}
        </Text>
        <Modal.SubmitFooter
          isValid
          buttonColor="red"
          close={() => setConfirmCloseOpen(false)}
          submitText={I18n.t('ai_qb_close_confirm_discard')}
          onSubmit={handleConfirmClose}
        />
      </Modal>
    </>
  );
};

export default ReviewQuestionsPanel;
